import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import PropTypes from 'prop-types';
import Layout from '@components/Layout';
import { NavContext } from '@components/navigation/Context';
import PageComponent from '@components/page/PageComponent';
import SimpleProductCarousel from '@components/storefront/SimpleProductCarousel';
import { DEFAULT_LOCALE } from '@constants';
import { HeaderData, PageSlice } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import handlePageRedirect from '@utils/handlePageRedirect';
import handleTextReplacement from '@utils/handleTextReplacement';
import { pageIsAdsPage } from '@utils/helpers';
import { mapPrismicHeaderLinksAlias, mapPrismicLinkAlias } from '@utils/mapPrismicAliases';
import useNavigate from '@utils/useNavigate';
import { ProductShoppingProvider } from './ProductShoppingContext';
import SharedProductHeader from './SharedProductHeader';

export const pageQuery = graphql`
  query ProductPage($uid: String!, $language: String) {
    locales: allLocale(filter: { language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    allPrismicEcommerceProduct(filter: { uid: { eq: $uid } }) {
      nodes {
        id
        uid
        _previewable
        lang
        data {
          hide_product_header
          title {
            text
          }
          short_description {
            text
            html
          }
          product_code {
            text
          }
          custom_products {
            product_id
          }
          klaviyo_id {
            text
          }
          payment_options_header {
            text
          }
          firework_integration_type
          firework_show_in_countries
          firework_playlist_id
          text_color
          page_header_color
          shareImage {
            url
          }
          description {
            text
          }
          banner_subtitle {
            text
          }
          banner_title {
            text
          }
          banner_enabled
          header_heading {
            text
          }
          header_feedback {
            feedback_image {
              alt
              gatsbyImageData
              url
            }
            feedback_text {
              text
            }
          }
          header_icons {
            icon {
              alt
              gatsbyImageData
              url
            }
            icon_text {
              text
            }
            icon_description {
              text
            }
          }
          header_images {
            order
            product_image {
              alt
              gatsbyImageData(width: 600, imgixParams: { q: 80 }, placeholder: BLURRED)
              url
            }
          }
          header_videos {
            thumbnail {
              alt
              gatsbyImageData
              url
            }
            video {
              url
            }
          }
          header_options {
            label {
              text
            }
            value {
              text
            }
          }
          top_subtitle {
            text
          }
          header_subtitle {
            text
          }
          header_text {
            html
          }
          header_subtext {
            html
          }
          header_links {
            text {
              text
            }
            link_alias: link {
              text
            }
          }
          header_trustpilot
          header_background_color
          header_background_gradient {
            text
          }
          header_background_image {
            alt
            gatsbyImageData
            url
          }
          header_button_type
          shop_disabled
          preselected_tampon_boxes
          learn_more_link {
            text
          }
          promo_ribbon_text {
            text
          }
          promo_ribbon_icon {
            alt
            gatsbyImageData
            url
          }
          is_using_new_design_system
          promo_ribbon_text_color
          promo_ribbon_background_color
          promo_ribbon_border_color
          product_header_faqs {
            show_in_countries {
              text
            }
            answer {
              richText
              text
            }
            question {
              text
            }
          }
          upsell_products_title
          upsell_products {
            upsell_product_code
          }
          bundle_products_title
          bundle_products {
            bundle_product_code
            bundle_image {
              alt
              gatsbyImageData
              url
            }
            bundle_description {
              text
            }
            badge_text {
              text
            }
          }
          vertical_icons
          product_options {
            label {
              text
            }
            title {
              text
            }
            one_off_sku {
              text
            }
            subscription_includes {
              text
            }
            subscription_plan {
              text
            }
            subscription_sku {
              text
            }
            option_info_json {
              text
            }
          }
          payment_options_title {
            text
          }
          addon_options_title {
            text
          }
          addon_products {
            text
          }
          configuration_title {
            text
          }
          body {
            ... on PrismicEcommerceProductDataBodyImageText {
              slice_type
              items {
                button_text {
                  text
                }
                button_link {
                  text
                }
                button_text_color
                button_color
                button_style
              }
              primary {
                hide
                text {
                  html
                }
                padding {
                  text
                }
                mobile_padding {
                  text
                }
                mobile_reverse
                layout
                gap {
                  text
                }
                mobile_gap {
                  text
                }
                mobile_text_align
                image_width {
                  text
                }
                mobile_image_width {
                  text
                }
                text_width {
                  text
                }
                breakpoint {
                  text
                }
                background_color
                background_gradient {
                  text
                }
                text_align
                text_color
                alignment
                image {
                  alt
                  url
                  gatsbyImageData
                }
                video {
                  url
                }
              }
            }
            ... on PrismicEcommerceProductDataBodySpacer {
              slice_type
              primary {
                height {
                  text
                }
                height_mobile {
                  text
                }
                background_color
                background {
                  text
                }
                hide_in_countries {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyCertificates {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyTrustpilot {
              slice_type
              primary {
                background_color
                text_color
                main_quote {
                  text
                }
                type
                tags {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyVideoHalfRow {
              primary {
                button_text {
                  text
                }
                background_css {
                  text
                }
                button_link {
                  text
                }
                desktop_placeholder_image {
                  gatsbyImageData
                  alt
                  url
                }
                text_color
                subtitle {
                  text
                }
                mobile_video {
                  url
                }
                heading {
                  text
                }
                desktop_video {
                  url
                }
                mobile_text_color
                mobile_placeholder_image {
                  gatsbyImageData
                  alt
                  url
                }
                text_info_type
                mobile_info_covers_video
                mobile_background_css {
                  text
                }
              }
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyCustomSlice {
              id
              slice_type
              primary {
                type
              }
            }
            ... on PrismicEcommerceProductDataBodyThreeStatistics {
              slice_type
              primary {
                heading {
                  text
                }
                background_color
                big_number_color
                button_text_key
                button_url
              }
              items {
                image {
                  url
                  alt
                }
                big_number
                stat_description {
                  text
                }
                source_url
              }
            }
            ... on PrismicEcommerceProductDataBodyImageGrid {
              primary {
                hide
              }
              items {
                image {
                  gatsbyImageData
                  alt
                }
              }
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyFeaturedIn {
              slice_type
              primary {
                featured_title {
                  text
                }
                type
                background_color
                text_color
                background_gradient {
                  text
                }
              }
              items {
                logo {
                  gatsbyImageData
                  alt
                }
                quote {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyIconTextGrid {
              slice_type
              primary {
                grid_title {
                  text
                }
                grid_title_rich {
                  richText
                }
                column_max_width {
                  text
                }
                gap {
                  text
                }
                icon_gap {
                  text
                }
                icon_gap_mobile {
                  text
                }
                gap_mobile {
                  text
                }
                alignment_desktop
                alignment_mobile
                columns {
                  text
                }
                columns_mobile {
                  text
                }
                image_width {
                  text
                }
                image_height {
                  text
                }
                padding {
                  text
                }
                mobile_padding {
                  text
                }
                background_color
                background_gradient {
                  text
                }
                text_color
                button_title {
                  text
                }
                button_link {
                  text
                }
              }
              items {
                image {
                  gatsbyImageData
                  alt
                }
                item_title {
                  text
                  richText
                }
                text {
                  html
                  richText
                }
                link_alias: link {
                  text
                }
                link_text {
                  text
                }
                type_of_link
              }
            }
            ... on PrismicEcommerceProductDataBodyText {
              slice_type
              primary {
                text {
                  html
                }
                maxwidth {
                  text
                }
                padding {
                  text
                }
                mobile_padding {
                  text
                }
                background_color
                background_gradient {
                  text
                }
                text_color
                text_align
              }
            }
            ... on PrismicEcommerceProductDataBodyImage {
              slice_type
              primary {
                image {
                  alt
                  gatsbyImageData
                }
                width {
                  text
                }
                height {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyTamponsModalRow {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodySubscriptionModule {
              slice_type
              primary {
                image {
                  gatsbyImageData
                  alt
                }
                show_sampler
                title {
                  text
                }
                ticks_title {
                  text
                }
                background_color
                text_color
              }
              items {
                feature {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyScienceModule {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyTestimonialsSlider {
              slice_type
              primary {
                background_color
                text_color
              }
              items {
                review_text {
                  text
                }
                review_cite {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyVideo {
              slice_type
              primary {
                cover
                height {
                  text
                }
                mobile_height {
                  text
                }
                video {
                  url
                }
                mobile_video {
                  url
                }
                width {
                  text
                }
                mobile_width {
                  text
                }
                background_color
              }
            }
            ... on PrismicEcommerceProductDataBodyFaqs {
              slice_type
              items {
                show_in_countries {
                  text
                }
                answer {
                  richText
                  text
                }
                question {
                  text
                }
              }
              primary {
                title {
                  text
                }
                background_color
                text_color
              }
            }
            ... on PrismicEcommerceProductDataBodyTour {
              slice_type
              primary {
                background_color
                text_color
                image {
                  alt
                  gatsbyImageData
                }
              }
              items {
                title {
                  text
                }
                text {
                  html
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyButtonRow {
              slice_type
              primary {
                background_color
                color
              }
              items {
                new_window
                button_text {
                  text
                }
                button_link {
                  text
                }
                scroll_id {
                  text
                }
                button_style
              }
            }
            ... on PrismicEcommerceProductDataBodyIdentifierRow {
              slice_type
              primary {
                id {
                  text
                }
                title1 {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyTextFeatureRow {
              slice_type
              primary {
                text_color
                text {
                  html
                }
                image {
                  gatsbyImageData
                  alt
                }
                feature_heading {
                  text
                  html
                }
              }
              items {
                text {
                  text
                }
                link_alias: link {
                  text
                }
                image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyEndorsementsRow {
              slice_type
              primary {
                background_color
                background_image {
                  url
                }
                title {
                  text
                }
                text_color
              }
              items {
                name {
                  text
                }
                profile_image {
                  gatsbyImageData
                  alt
                }
                quote {
                  text
                }
                qualification {
                  text
                }
                title {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyZeroWaste {
              slice_type
              primary {
                title_one {
                  text
                }
                title_two {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyCaseStudySection {
              slice_type
              items {
                research_description {
                  richText
                }
                link_alias: link {
                  richText
                }
                research_title {
                  text
                }
                researcher {
                  text
                }
              }
              primary {
                color
                background_color
                padding {
                  text
                }
                section_title {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyVmsFounderSResults {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyStiFoundersResults {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyHpvFoundersResults {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyVmsSpecialistCare {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyVmsVaginalFlora {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyVmsAtHome {
              slice_type
              primary {
                at_home_button {
                  text
                }
                at_home_button_link {
                  url
                }
                at_home_disclaimer {
                  text
                }
                at_home_image {
                  url
                }
                at_home_subtitle {
                  text
                }
                at_home_text {
                  text
                }
                at_home_background {
                  text
                }
                at_home_padding {
                  text
                }
                at_home_mobile_padding {
                  text
                }
              }
              items {
                icon {
                  url
                }
                item {
                  text
                }
                short {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyGiftCardForm {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyComparisonTable {
              slice_type
              primary {
                type
                table_title {
                  text
                }
                has_prices
                price_optibac {
                  text
                }
                image {
                  gatsbyImageData
                  alt
                }
                background_image {
                  url
                  gatsbyImageData
                  alt
                }
                price_seed {
                  text
                }
                price_garden_of_life {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyImageSlider {
              slice_type
              primary {
                arrows
                pagination
                max_width {
                  text
                }
                title {
                  richText
                }
                text_color
                background_color
                background_gradient {
                  text
                }
              }
              items {
                image {
                  url
                  gatsbyImageData
                  alt
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyVitalsSection {
              slice_type
              primary {
                article_ids {
                  text
                }
                hide_controls
              }
            }
            ... on PrismicEcommerceProductDataBodyVmsMicrobiomeVideo {
              slice_type
              primary {
                microbiome_image {
                  url
                }
                microbiome_title {
                  text
                }
                microbiome_text {
                  text
                }
                microbiome_more {
                  text
                }
                microbiome_video {
                  url
                }
                youtube_video_id {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyNewsletter {
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyImageTextBulletList {
              slice_type
              items {
                bullet_icon {
                  alt
                  url
                  gatsbyImageData
                }
                bullet_text {
                  text
                }
                short_text {
                  text
                }
              }
              primary {
                text {
                  html
                }
                subtext {
                  html
                }
                button_text {
                  text
                }
                button_path {
                  text
                }
                horizontal_mobile_bullets
                background_color
                padding {
                  text
                }
                padding_mobile {
                  text
                }
                mobile_reverse
                layout
                gap {
                  text
                }
                main_text_color
                image_width {
                  text
                }
                mobile_image_width {
                  text
                }
                text_width {
                  text
                }
                text_align
                alignment
                image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyTwoColumnImageAndText {
              slice_type
              primary {
                main_text_key
                secondary_text_key
                padding_style
                background_style
                image {
                  alt
                  url
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyIconDescriptionBoxes {
              id
              items {
                link_text_key
                link {
                  target
                  url
                }
                main_text_key
                huge_text_key
                background_style
                icon {
                  url
                  gatsbyImageData
                  alt
                  dimensions {
                    width
                    height
                  }
                  url
                }
              }
              slice_type
              primary {
                main_text_font_size
                mobile_main_text_font_size
                background_style
                column_max_width {
                  text
                }
                heading_icon {
                  alt
                  gatsbyImageData
                  dimensions {
                    width
                    height
                  }
                  url
                }
                heading_text_key
                padding_style
              }
            }
            ... on PrismicEcommerceProductDataBodyHowItWorksSteps {
              slice_type
              primary {
                heading_text_key
              }
              items {
                main_text_key
                info_text_key
                video_link_text_key
                video_preview_image {
                  url
                  alt
                }
                image {
                  url
                  alt
                }
                video_url {
                  url
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyGrid {
              slice_type
              primary {
                overline
                heading {
                  text
                }
                subheading {
                  text
                }
                buttonlink {
                  url
                }
                background_text
                variant
                buttontext {
                  text
                }
              }
              items {
                image {
                  url
                  alt
                }
                mobile_image {
                  url
                  alt
                }
                heading {
                  text
                  richText
                }
                mobile_heading {
                  text
                  richText
                }
                bodycopy {
                  text
                }
                mobile_bodycopy {
                  text
                }
                supbodycopy {
                  text
                }
                learn_more_url
              }
            }
            ... on PrismicEcommerceProductDataBodyInnerButtonSlider {
              slice_type
              primary {
                background_text
              }
              items {
                overline
                image {
                  url
                  alt
                  gatsbyImageData
                }
                heading {
                  text
                  richText
                }
                bodycopy {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyProductCompareCards {
              slice_type
              primary {
                heading {
                  text
                  richText
                }
                products_to_include
                screening_type
              }
            }
            ... on PrismicEcommerceProductDataBodyTimeline {
              slice_type
              primary {
                overline
                heading {
                  text
                  richText
                }
                subheading {
                  text
                }
                button_url
                button_text_key
              }
              items {
                overline
                heading {
                  text
                  richText
                }
                bodycopy {
                  text
                }
                video_preview_image {
                  url
                  alt
                  gatsbyImageData
                }
                image {
                  url
                  alt
                  gatsbyImageData
                }
                video_url {
                  url
                }
                video_link_text_key
                link_icon
              }
            }
            ... on PrismicEcommerceProductDataBodyPathogenList {
              slice_type
              primary {
                overline
                heading {
                  text
                  richText
                }
                subheading {
                  text
                }
                screening_type
                image {
                  url
                  alt
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyHeroBanner {
              slice_type
              primary {
                background_text
                overline
                image {
                  url
                  alt
                }
                aux_image {
                  url
                  alt
                }
                heading {
                  text
                  richText
                }
                bodycopy {
                  text
                }
                button_url
                button_label
                breakpoint_px
                no_mobile_image
              }
            }
            ... on PrismicEcommerceProductDataBodyPositivesAndImage {
              slice_type
              primary {
                heading_text_key
                background_style
                mobile_background_style
                image {
                  url
                  gatsbyImageData
                  alt
                }
              }
              items {
                main_text_key
              }
            }
            ... on PrismicEcommerceProductDataBodyScreeningParametersInfoSlider {
              slice_type
              primary {
                type
              }
            }
            ... on PrismicEcommerceProductDataBodyVitalsCarousel {
              slice_type
              primary {
                article_ids_string
                hide_title
                show_read_more_links
                heading {
                  text
                }
                background {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyVideoGrid {
              slice_type
              primary {
                grid_title {
                  text
                }
                trustpilot
                title_text_color
                frame
                scroll_elements_desktop
                gap {
                  text
                }
                gap_mobile {
                  text
                }
                rounded_corners
                text_align_all
              }
              items {
                thumbnail {
                  url
                  gatsbyImageData
                  alt
                }
                video {
                  url
                }
                text {
                  text
                }
                video_width {
                  text
                }
                video_height {
                  text
                }
                bottom_text
              }
            }
            ... on PrismicEcommerceProductDataBodyLinkCarousel {
              slice_type
              primary {
                background {
                  text
                }
                background_image {
                  url
                  gatsbyImageData
                  alt
                }
              }
              items {
                background {
                  text
                }
                heading {
                  text
                }
                subtitle {
                  text
                }
                mobile_subtitle {
                  text
                }
                image {
                  url
                  gatsbyImageData
                  alt
                }
                link_text {
                  text
                }
                link_alias: link {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyHeadingMessage {
              slice_type
              primary {
                heading_text {
                  richText
                  text
                }
                background_color
                text_color1
                top_image {
                  url
                  gatsbyImageData
                  alt
                }
                bottom_image {
                  url
                  gatsbyImageData
                  alt
                }
                heading_vertical_padding {
                  text
                }
                heading_vertical_padding_mobile {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyProductComparisonTable {
              slice_type
              primary {
                background_color
                explainer_background_color
                table_json {
                  text
                }
                table_header_text {
                  text
                }
                row_title {
                  text
                }
                highlighted_discount {
                  text
                }
                highlighted_column_index
                explainer_text {
                  richText
                }
                explainer_image {
                  gatsbyImageData
                  alt
                  dimensions {
                    width
                    height
                  }
                  url
                }
              }
              id
            }
            ... on PrismicEcommerceProductDataBodyStepSlider {
              id
              slice_type
              primary {
                heading_text_key
              }
              items {
                text_color
                background_color
                image {
                  url
                  gatsbyImageData
                  dimensions {
                    width
                    height
                  }
                  alt
                }
                title_key
                text_key
              }
            }
            ... on PrismicEcommerceProductDataBodyConsultationsExplained {
              id
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyConsultationsMeetNurses {
              id
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyFeaturedProducts {
              slice_type
              primary {
                background_color
                product_ids {
                  text
                }
                title_key
              }
            }
            ... on PrismicEcommerceProductDataBodyFirework {
              primary {
                type
                max_width {
                  text
                }
                playlist_id
                background_color
                show_in_countries {
                  text
                }
              }
              slice_type
            }
            ... on PrismicEcommerceProductDataBodyHeroFullWidthImage {
              slice_type
              primary {
                heading {
                  richText
                  text
                  html
                }
                subheading {
                  richText
                  text
                  html
                }
                show_starting_at
                fsa_hsa_eligible
                button_url
                button_text_key
                dark_text
                dark_button
                align_top_mobile
                background_image {
                  url
                }
                background_image_mobile {
                  url
                }
                product_code
              }
            }
            ... on PrismicEcommerceProductDataBodyImageAndContent {
              id
              slice_type
              primary {
                background_color
                overline
                heading {
                  richText
                }
                subheading {
                  richText
                }
                centered_text
                image_full_background
                button_url
                button_text_key
                product_code1
                price_show_in_countries
                fsa_hsa_eligible
                aux_image {
                  url
                }
                image {
                  url
                }
                image_mobile {
                  url
                }
                notice
                reverse_order
                reverse_order_mobile
                original_image_aspect_ratio
                image_max_width_tablet
                show_in_countries {
                  text
                }
                minimum_height
              }
              items {
                icon {
                  url
                  alt
                }
                icon_text
              }
            }
            ... on PrismicEcommerceProductDataBodyTabsFeature {
              id
              slice_type
              primary {
                overline
                heading {
                  richText
                }
                background_color
                centered_text
              }
              items {
                icon {
                  url
                  alt
                }
                icon_text
                image {
                  url
                }
                image_mobile {
                  url
                }
                item_heading {
                  richText
                }
                item_subheading {
                  richText
                }
                button_url
                button_text_key
                item_centered_text
                item_reverse_order
                item_reverse_order_mobile
                background_full_width
                background_color
                background_image {
                  url
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyCarouselModule {
              slice_type
              primary {
                background_color
                reverse_order_mobile
              }
              items {
                overline
                heading {
                  richText
                }
                subheading {
                  richText
                }
                button_url
                button_text_key
                image {
                  url
                  alt
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyTestimonialsModule {
              slice_type
              primary {
                overline
                heading {
                  richText
                }
                subheading {
                  richText
                }
                button_text_key
                button_url
                background_color
                item_color
              }
              items {
                image {
                  url
                }
                testimonial {
                  richText
                }
                person_name
              }
            }
            ... on PrismicEcommerceProductDataBodyImageTextQuadrant {
              slice_type
              primary {
                heading {
                  html
                }
                background_gradient {
                  text
                }
                background_color
                image_width {
                  text
                }
                mobile_image_width {
                  text
                }
                main_image {
                  alt
                  url
                  gatsbyImageData
                }
              }
              items {
                item_title_icon {
                  alt
                  url
                  gatsbyImageData
                }
                item_title {
                  text
                }
                item_title_background_color
                item_description {
                  text
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyStripBanner {
              slice_type
              primary {
                background_color
                background_gradient {
                  text
                }
              }
              items {
                title1 {
                  text
                }
                image {
                  url
                  alt
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyQuoteText {
              slice_type
              primary {
                overline1
                heading {
                  richText
                }
                subheading {
                  richText
                }
                background_color
              }
              items {
                name {
                  text
                }
                qualification {
                  text
                }
                main_quote {
                  text
                }
                quote {
                  text
                }
                profile_image {
                  url
                  alt
                  gatsbyImageData
                }
              }
            }
            ... on PrismicEcommerceProductDataBodyHeaderImage {
              slice_type
              primary {
                button_text_key
                button_url
                background_color
                product_code
                subheading {
                  richText
                  text
                }
                heading {
                  richText
                  text
                }
                coming_soon
                waitlist_code
                waitlist_eu
                waitlist_us
                centered_text
                fsa_hsa_eligible
                pin_image_right
                image {
                  alt
                  url
                  gatsbyImageData
                }
                mobile_image {
                  alt
                  url
                  gatsbyImageData
                }
              }
            }
          }
        }
      }
    }
  }
`;

const ProductPage = ({ data, pageContext }: { data: any; pageContext: { language: string } }) => {
  const { allPrismicEcommerceProduct } = data;
  const { language } = pageContext;

  const getPageData = () => {
    const pages = allPrismicEcommerceProduct.nodes;
    const currentLocale = pages.find((page: { lang: string }) => page.lang === language);
    const fallbackLocale = pages.find((page: { lang: string }) => page.lang === DEFAULT_LOCALE);

    return currentLocale || fallbackLocale;
  };

  const [productPageObject, setProductPageObject] = useState(getPageData());

  // Update pageObject when data changes (for previews)
  useEffect(() => {
    setProductPageObject(getPageData());
  }, [allPrismicEcommerceProduct]);

  const { country } = useCountry();
  const { navigateWrapper } = useNavigate();
  const { navDispatch } = useContext(NavContext);

  const {
    uid,
    data: {
      body,
      title,
      description,
      shareImage,
      custom_products,
      product_code,
      text_color,
      hide_product_header,
      page_header_color,
      header_background_color,
      header_background_gradient,
      header_background_image,
      header_button_type,
      shop_disabled,
      preselected_tampon_boxes,
      learn_more_link,
      vertical_icons,
      banner_enabled,
      banner_subtitle,
      banner_title,
      top_subtitle,
      header_feedback,
      header_heading,
      header_icons,
      header_images,
      header_subtitle,
      header_text,
      header_subtext,
      header_trustpilot,
      header_videos,
      header_options,
      header_links,
      klaviyo_id,
      payment_options_header,
      firework_integration_type,
      firework_show_in_countries,
      firework_playlist_id,
      promo_ribbon_text,
      promo_ribbon_icon,
      promo_ribbon_text_color,
      promo_ribbon_background_color,
      promo_ribbon_border_color,
      product_options,
      payment_options_title,
      addon_options_title,
      addon_products,
      configuration_title,
      upsell_products_title,
      upsell_products,
      bundle_products_title,
      bundle_products,
      product_header_faqs,
      is_using_new_design_system
    }
  } = productPageObject;

  productPageObject.data.body = mapPrismicLinkAlias(productPageObject.data.body);
  productPageObject.data.header_links = mapPrismicHeaderLinksAlias(productPageObject.data.header_links);

  const preselectedTamponBoxes = uid === 'tampon-boxes';

  useEffect(() => {
    if (page_header_color) navDispatch({ type: 'setColor', data: page_header_color });
  }, []);

  useEffect(() => {
    handlePageRedirect(uid, country, navigateWrapper);
  }, [country]);

  const headerData: HeaderData = {
    hideProductHeader: hide_product_header,
    topSubtitle: top_subtitle?.text,
    heading: header_heading.text,
    subtitle: header_subtitle.text,
    text: header_text.html,
    subtext: header_subtext.html,
    productCode: product_code.text,
    textColor: text_color,
    background: header_background_gradient?.text || header_background_color || `url(${header_background_image.url})`,
    buttonType: header_button_type,
    shopDisabled: shop_disabled,
    preselectedTamponBoxes: preselected_tampon_boxes,
    learnMoreLink: learn_more_link,
    verticalIcons: vertical_icons,
    trustpilot: header_trustpilot,
    feedback: header_feedback,
    icons: header_icons,
    images: header_images,
    videos: header_videos,
    options: header_options,
    links: header_links,
    klaviyoId: klaviyo_id?.text ?? '',
    payment_options_header,
    upsell_products_title,
    upsell_products,
    bundle_products_title,
    bundle_products,
    product_header_faqs,
    is_using_new_design_system,
    promoRibbon: {
      text: promo_ribbon_text.text,
      icon: promo_ribbon_icon,
      borderColor: promo_ribbon_border_color,
      backgroundColor: promo_ribbon_background_color,
      textColor: promo_ribbon_text_color
    },
    productOptions: product_options,
    paymentOptionsTitle: payment_options_title?.text ?? '',
    addonOptionsTitle: addon_options_title?.text ?? '',
    addonProducts: addon_products?.text ?? '',
    configurationTitle: configuration_title?.text ?? '',
    fireworkIntegrationType: firework_integration_type ?? 'none',
    fireworkShowInCountries: firework_show_in_countries ?? '',
    fireworkPlaylistId: firework_playlist_id ?? '',
    fireworkWidgetSize: 'medium'
  };
  let stickyBannerData;
  if (banner_enabled) {
    stickyBannerData = {
      productCode: product_code.text,
      title: banner_title.text,
      subtitle: banner_subtitle.text,
      textColor: text_color
    };
  }

  const location = useLocation();
  const organicHempPadsURL = '/products/organic-hemp-pads';
  const organicTamponsLandingURL = '/products/organic-tampons';
  const stiLandingURL = '/products/at-home-sti-screening';
  const vmsLandingURL = '/products/at-home-vaginal-screening';
  const hpvLandingURL = '/products/at-home-hpv-screening';
  const provioticsLandingURL = '/products/vaginal-health-proviotics';
  const metaFriendlyTamponsURL = '/products/cbd-tampon';
  const consultationLandingURL = '/products/nurse-consultation';
  const provioticsURL = '/products/proviotics';
  const hormonalTestURL = '/products/hormonal-testing';
  const isPageForAds = pageIsAdsPage(location.pathname);
  const firstImageSlide = header_images.find((slide: { product_image: any }) => slide.product_image);

  const showProductsCarousel = !(
    !location ||
    location?.pathname?.includes(organicTamponsLandingURL) ||
    location?.pathname?.includes(provioticsLandingURL) ||
    location?.pathname?.includes(stiLandingURL) ||
    location?.pathname?.includes(vmsLandingURL) ||
    location?.pathname?.includes(hpvLandingURL) ||
    location?.pathname?.includes(consultationLandingURL) ||
    location?.pathname?.includes(metaFriendlyTamponsURL) ||
    location?.pathname?.includes(organicHempPadsURL) ||
    location?.pathname?.includes(provioticsURL) ||
    location?.pathname?.includes(hormonalTestURL) ||
    isPageForAds
  );

  const localisedTitle = handleTextReplacement(title.text);

  const SEO = {
    uid,
    href: `/products/${uid}`,
    title: localisedTitle,
    description: description.text,
    image: shareImage.url,
    productData: headerData,
    preloadImage: firstImageSlide?.product_image?.url
  };

  return (
    <Layout SEO={SEO}>
      {!headerData.hideProductHeader && (
        <ProductShoppingProvider productHeaderData={headerData} preselectedTamponBoxes={preselectedTamponBoxes} stickyBanner={stickyBannerData}>
          <SharedProductHeader />
        </ProductShoppingProvider>
      )}
      {body.map((slice: PageSlice, index: number) => {
        if (slice.primary && 'hide' in slice.primary && slice.primary.hide) return null;
        const sliceKey = slice.id || `${slice.slice_type}-${index}`;
        return <PageComponent key={sliceKey} slice={slice} country={country} />;
      })}
      {showProductsCarousel ? <SimpleProductCarousel productCode={product_code.text} customProducts={custom_products} country={country} /> : null}
    </Layout>
  );
};

export default withPrismicPreview(ProductPage);

ProductPage.propTypes = {
  data: PropTypes.shape({
    allPrismicEcommerceProduct: PropTypes.object.isRequired
  }).isRequired,
  pageContext: PropTypes.shape({
    language: PropTypes.string.isRequired
  }).isRequired
};
